'use client';

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Sparkles, RefreshCw } from 'lucide-react';

interface Message {
  id: number;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

interface ChatHistory {
  role: 'user' | 'assistant';
  content: string;
}

const initialMessages: Message[] = [
  {
    id: 1,
    text: "Bonjour ! 👋 Je suis l'Assistant Expert du CERAD-CI, propulsé par l'IA. Comment puis-je vous aider aujourd'hui ?",
    isBot: true,
    timestamp: new Date(),
  },
];

const quickQuestions = [
  "Quels sont vos services ?",
  "Comment demander un devis ?",
  "Parlez-moi des formations",
  "Qu'est-ce que la GAR ?",
];

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (text: string = input) => {
    if (!text.trim() || isTyping) return;

    const userMessage: Message = {
      id: Date.now(),
      text: text.trim(),
      isBot: false,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Update chat history for context
    const newHistory: ChatHistory[] = [
      ...chatHistory,
      { role: 'user', content: text.trim() },
    ];

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          history: newHistory.slice(-10), // Keep last 10 messages for context
        }),
      });

      const data = await response.json();

      const botMessage: Message = {
        id: Date.now() + 1,
        text: data.response || "Désolé, je n'ai pas pu traiter votre demande. Veuillez réessayer.",
        isBot: true,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
      setChatHistory([
        ...newHistory,
        { role: 'assistant', content: data.response },
      ]);
    } catch {
      const errorMessage: Message = {
        id: Date.now() + 1,
        text: "Désolé, une erreur s'est produite. Veuillez réessayer ou nous contacter directement.",
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const resetChat = () => {
    setMessages(initialMessages);
    setChatHistory([]);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-400 shadow-2xl flex items-center justify-center text-white animate-pulse-orange ${isOpen ? 'hidden' : ''
          }`}
      >
        <MessageCircle className="w-7 h-7" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-gray-100"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-cerad-green to-cerad-green-light p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center relative">
                    <Bot className="w-6 h-6" />
                    <Sparkles className="w-3 h-3 absolute -top-1 -right-1 text-cerad-gold" />
                  </div>
                  <div>
                    <h3 className="font-montserrat font-bold flex items-center gap-2">
                      Assistant Expert
                      <span className="text-[10px] bg-cerad-gold/30 px-2 py-0.5 rounded-full">IA</span>
                    </h3>
                    <p className="text-sm text-white/80">CERAD-CI • En ligne</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={resetChat}
                    title="Nouvelle conversation"
                    aria-label="Nouvelle conversation"
                    className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    title="Fermer le chat"
                    aria-label="Fermer le chat"
                    className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-end gap-2 ${message.isBot ? '' : 'flex-row-reverse'}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${message.isBot
                        ? 'bg-cerad-green/10 text-cerad-green'
                        : 'bg-cerad-gold/10 text-cerad-gold'
                      }`}
                  >
                    {message.isBot ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                  </div>
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl ${message.isBot
                        ? 'bg-gray-100 text-gray-800 rounded-bl-md'
                        : 'bg-cerad-green text-white rounded-br-md'
                      }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                  </div>
                </motion.div>
              ))}
              <div ref={messagesEndRef} />

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2"
                >
                  <div className="w-8 h-8 rounded-full bg-cerad-green/10 text-cerad-green flex items-center justify-center">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 bg-cerad-green rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-2 h-2 bg-cerad-green rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 bg-cerad-green rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Quick Questions */}
            {messages.length <= 2 && !isTyping && (
              <div className="px-4 pb-3">
                <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Questions suggérées :
                </p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((question) => (
                    <button
                      key={question}
                      onClick={() => handleSend(question)}
                      disabled={isTyping}
                      className="px-3 py-1.5 text-xs bg-cerad-green/5 text-cerad-green rounded-full hover:bg-cerad-green/10 transition-colors disabled:opacity-50"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-gray-100 bg-gray-50/50">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Posez votre question..."
                  disabled={isTyping}
                  className="flex-1 px-4 py-3 rounded-full bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-cerad-green/20 focus:border-cerad-green text-sm disabled:bg-gray-100 transition-all"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isTyping}
                  title="Envoyer"
                  aria-label="Envoyer le message"
                  className="w-12 h-12 rounded-full bg-gradient-to-br from-cerad-green to-cerad-green-light text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all active:scale-95"
                >
                  <Send className="w-5 h-5" />
                </button>
              </form>
              <p className="text-[10px] text-gray-400 text-center mt-2">
                Propulsé par Gemini AI • CERAD-CI
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}